import type { Ingredient, UnitSystem, Vessel } from '@/types';
import { formatVessel, MAIN_VESSEL, selectVessel } from '@/vessels';
import { Checkbox } from './Checkbox';
import { DirectionSegments } from './DirectionSegments';

/** Bullets pre-filtered + normalized to strings by the page before rendering. */
interface RenderableDirection {
  title: string;
  bullets: string[];
  perVessel?: boolean;
}

interface Props {
  direction: RenderableDirection;
  checkedBullets: boolean[];
  onToggleBullet: (bulletIdx: number) => void;
  onToggleDirection: () => void;
  ingredientsByName: Map<string, Ingredient>;
  multiplier: number;
  unitSystem: UnitSystem;
  showAmounts: boolean;
  vessels?: Record<string, Vessel> | undefined;
}

export function Direction({
  direction,
  checkedBullets,
  onToggleBullet,
  onToggleDirection,
  ingredientsByName,
  multiplier,
  unitSystem,
  showAmounts,
  vessels,
}: Props) {
  const hasBullets = direction.bullets.length > 1;
  const allChecked = checkedBullets.every(Boolean);

  const selections = new Map(
    Object.entries(vessels ?? {}).map(([key, v]) => [key, selectVessel(v, multiplier)]),
  );
  const main = selections.get(MAIN_VESSEL);
  const perVessel = direction.perVessel === true && main !== undefined;
  const effectiveMultiplier = perVessel ? multiplier / main.count : multiplier;
  // Under perVessel the bullets describe one main vessel, so it reads singular
  // there; every other vessel always renders its full selection.
  const vesselTexts = Object.fromEntries(
    [...selections].map(([key, sel]) => [
      key,
      formatVessel(perVessel && key === MAIN_VESSEL ? { ...sel, count: 1 } : sel, unitSystem),
    ]),
  );
  const subtitle =
    perVessel && main.count > 1 ? `Repeat in each of ${formatVessel(main, unitSystem)}` : undefined;

  return (
    <li class={`step ${allChecked ? 'step--done' : ''}`}>
      <button type="button" class="step__head" onClick={onToggleDirection}>
        <Checkbox checked={allChecked} />
        <span class="step__title">{direction.title}</span>
      </button>
      {subtitle ? <div class="step__subtitle">{subtitle}</div> : null}
      <ul class="step__bullets">
        {direction.bullets.map((bullet, i) => (
          <li
            key={i}
            class={`step__bullet ${hasBullets && checkedBullets[i] ? 'step__bullet--done' : ''}`}
            onClick={hasBullets ? () => onToggleBullet(i) : undefined}
            style={hasBullets ? undefined : { cursor: 'default' }}
          >
            {hasBullets ? (
              <Checkbox checked={checkedBullets[i] ?? false} />
            ) : (
              <span style={{ width: 20 }} />
            )}
            <span>
              <DirectionSegments
                bullet={bullet}
                ingredientsByName={ingredientsByName}
                multiplier={effectiveMultiplier}
                unitSystem={unitSystem}
                showAmounts={showAmounts}
                vesselTexts={vesselTexts}
              />
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}
